import { useState, useMemo, useEffect } from "react";
import Modal from "../Modal";
import { useDispatch, useSelector } from "react-redux";
import { setLoader } from "../../features/LoaderSlice";
import useNotification from "../../hooks/useNotification";
import axiosInstance from "../../api/axios";
import styles from "./styles.module.css";
import { useIntl } from "react-intl";
import OptOutCaptureForm from "./OptOutCaptureForm";
import moment from "moment";
import useTracker from "../../hooks/useTracker";
import UnsavedChangesModal from "../UnsavedChangesModal";
import PropTypes from 'prop-types';

const OptOutModal = ({
  getList,
  data: passData = {},
  open,
  setOpen,
  isLi = false,
  userDetails,
}) => {
  const intl = useIntl();
  const { successToast, errorToast } = useNotification();
  const dispatch = useDispatch();
  const { optOutTracker } = useTracker();
  const isValidPropData = (d) => {
    if (!d) return false;
    if (d.deactivate_all_li === true) return true;
    if (d.global_purpose) return true;
    if (d.deactivate_all_consent === true) return true;
    return false;
  };
  const trackerDetails = useSelector((state) => state?.TrackerReducer);
  const dataTrack = useSelector((state) => state?.TrackerReducer?.form?.data);
  const [captureDate, setCaptureDate] = useState(
    trackerDetails?.form?.capture_date || null,
  );
  const [captureTime, setCaptureTime] = useState(
    trackerDetails?.form?.capture_time || "",
  );
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [unsavedDialogue, setUnsavedDialogue] = useState(false);
  const timezone = useSelector(
    (state) => state?.controllerConfig?.controllerConfig?.time_zone || "UTC",
  );
  const [data, setData] = useState(null)
  useEffect(() => {
    if (open)
      if (isValidPropData(passData))
        setData(passData)
      else if (isValidPropData(dataTrack))
        setData(dataTrack)
  }, [passData, dataTrack, open])


  useEffect(() => {
    const trackerCaptureDate = trackerDetails?.form?.capture_date;
    const trackerCaptureTime = trackerDetails?.form?.capture_time;
    if (!open && (trackerCaptureDate || trackerCaptureTime)) {
      setOpen(true);
    }
  }, []);
  const setTracker = (date, time) => {
    let cpData = ""
    if (date || time) {
      if (isValidPropData(passData))
        cpData = passData
      else if (isValidPropData(data)) cpData = data
    }
    optOutTracker({
      capture_date: date || "",
      capture_time: time || "",
      data: cpData
    });
  };
  const handleCaptureDateChange = (date) => {
    setTracker(date, captureTime);
    setCaptureDate(date);
  };
  const handleCaptureTimeChange = (time) => {
    setTracker(captureDate, time);
    setCaptureTime(time);
  };

  const handleOptOut = () => {
    setHasSubmitted(true);
    if (!captureDate) return;
    const digital_identity = {
      controller_code: userDetails?.controller_code,
      type: userDetails?.type,
      value: userDetails?.value,
      last_update_channel: "trumanUI",
    };
    if (!data?.deactivate_all_li) {
      digital_identity.customer_scope = userDetails?.customer_scope;
      digital_identity.language = userDetails?.language;
    }
    const payload = {
      digital_identity,
      unsubscribe_trigger_brand_id: "manual",
      unsubscribe_trigger_content_id: "manual",
    };
    if (captureDate)
      payload.capture_date = moment(captureDate).format("YYYY-MM-DD")
    if (captureTime)
      payload.capture_time = moment(captureTime).format("HH:mm:ss.SSS")
    if (data?.deactivate_all_consent)
      //all consents
      payload.deactivate_all_consent = true;
    else if (data?.deactivate_all_li)
      // all lis
      payload.deactivate_all = true;
    // consent or li Processing Purpose
    else
      payload.deactivate_data_processing_authorizations = [
        data?.global_purpose,
      ];
    const isConsentApi = !data?.deactivate_all_li;
    dispatch(setLoader({ loading: true, label: "updating..." }));
    axiosInstance
      .post(
        `/${isConsentApi ? "unsubscribe_consent" : "unsubscribe_all"
        }/authenticated`,
        payload
      )
      .then(() => {
        dispatch(setLoader({ loading: false, label: "" }));
        successToast(
          `${userDetails?.customer_scope?.toUpperCase()} has been successfully opted out`,
          3000
        );
        handleUnsaveDiscard();
        if (getList) getList();
      })
      .catch((error) => {
        dispatch(setLoader({ loading: false, label: "" }));
        let errorMessage = error?.data?.message || error?.message || "";
        if (!errorMessage) {
          errorMessage = `${userDetails?.customer_scope?.toUpperCase()} opt out failed, please try again later`;
        }
        errorToast(errorMessage, 3000);
      });
  };
  const handleSetOpen = (status) => {
    if (!status) {
      if (captureDate || captureTime) {
        setUnsavedDialogue(true);
        return;
      }
    }
    setOpen(status);
  };
  const handleUnsaveCancel = () => {
    setUnsavedDialogue(false);
  };
  const handleUnsaveDiscard = () => {
    setTracker();
    setCaptureDate(null);
    setCaptureTime("");
    setOpen(false);
    setUnsavedDialogue(false);
    setHasSubmitted(false)
  };
  const heading = useMemo(() => {
    if (!open) return ""
    if (data?.deactivate_all_li)
      return intl.formatMessage(
        {
          id: "opt-out.all-title",
          defaultMessage: "Are you sure {scope} wants to object to all?",
        },
        { scope: userDetails?.customer_scope?.toUpperCase() }
      );
    else if (data?.deactivate_all_consent)
      return intl.formatMessage(
        {
          id: "opt-out.all-consent-title",
          defaultMessage:
            "Are you sure {scope} wants to opt out all Bayer communication?",
        },
        { scope: userDetails?.customer_scope?.toUpperCase() }
      );
    else if (isLi)
      return intl.formatMessage(
        {
          id: "opt-out.li-title",
          defaultMessage: "Are you sure {scope} wants to object?",
        },
        { scope: userDetails?.customer_scope?.toUpperCase() }
      );
    else
      return intl.formatMessage(
        {
          id: "opt-out.consent-title",
          defaultMessage: "Are you sure {scope} wants to opt out?",
        },
        { scope: userDetails?.customer_scope?.toUpperCase() }
      );
  }, [intl, isLi, data, userDetails, open]);
  const description = useMemo(() => {
    if (!open) return ""
    if (data?.deactivate_all_li)
      return intl.formatMessage(
        {
          id: "opt-out.all-description-with-tz",
          defaultMessage:
            "By clicking on confirm you will opt out {name} from all legitimate interests and any consent they have provided. Kindly review and update the capture date and time if needed. Please provide date and time in {timezone} time zone",
        },
        {
          name: `${userDetails?.first_name ?? ""} ${userDetails?.last_name ?? ""}`,
          timezone: timezone
        }
      );
    else if (data?.deactivate_all_consent)
      return intl.formatMessage(
        {
          id: "opt-out.all-consent-description-with-tz",
          defaultMessage:
            "By clicking on confirm you will opt out {name} from all Bayer communication. Kindly review and update the capture date and time if needed. Please provide date and time in {timezone} time zone",
        },
        {
          scope: userDetails?.customer_scope?.toUpperCase(),
          name: `${userDetails?.first_name ?? ""} ${userDetails?.last_name ?? ""}`, timezone: timezone
        }
      );
    else if (isLi)
      return intl.formatMessage(
        {
          id: "opt-out.li-description-with-tz",
          defaultMessage:
            "By clicking on confirm you will opt out {name} from {purpose}. Kindly review and update the capture date and time if needed. Please provide date and time in {timezone} time zone",
        },
        {
          name: `${userDetails?.first_name ?? ""} ${userDetails?.last_name ?? ""}`,
          timezone: timezone,
          purpose: data?.global_purpose,
        },
      );
    else
      return intl.formatMessage(
        {
          id: "opt-out.consent-description-with-tz",
          defaultMessage:
            "By clicking on confirm you will opt out {name} from approved {purpose}. Kindly review and update the capture date and time if needed. Please provide date and time in {timezone} time zone",
        },
        {
          scope: userDetails?.customer_scope?.toUpperCase(),
          name: `${userDetails?.first_name ?? ""} ${userDetails?.last_name ?? ""}`,
          purpose: data?.global_purpose,
          timezone: timezone
        }
      );
  }, [intl, isLi, data, userDetails, timezone, open]);
  const permanentActionWarningMessage = useMemo(() => {
    return intl.formatMessage(
      {
        id: "opt-out.all-action-warning-with-scope",
        defaultMessage:
          "This action is permanent and cannot be undone without getting a new consent from the {scope}",
      },
      {
        scope: userDetails?.customer_scope?.toUpperCase(),
      },
    );
  }, [intl, userDetails]);
  if (!open) return null;
  return (
    <>
      <Modal
        title={intl.formatMessage({
          id: "opt-out.title",
          defaultMessage: "Opt out Confirmation",
        })}
        open={open}
        setOpen={handleSetOpen}
        okText={intl.formatMessage({
          id: "label.confirm",
          defaultMessage: "Confirm",
        })}
        canelText={intl.formatMessage({
          id: "label.cancel",
          defaultMessage: "Cancel",
        })}
        onSubmit={handleOptOut}
        isDisabled={!userDetails?.value}
      >
        <h2 className={styles["opt-out-heading"]}>{heading}</h2>
        <p className={styles["opt-out-message"]}>{description}</p>
        <OptOutCaptureForm
          timezone={timezone}
          captureDate={captureDate}
          setCaptureDate={handleCaptureDateChange}
          captureTime={captureTime}
          setCaptureTime={handleCaptureTimeChange}
          hasSubmitted={hasSubmitted}
        />
        <p className={`mt-3 ${styles["opt-out-message"]}`}>
          {permanentActionWarningMessage}
        </p>
      </Modal>
      <UnsavedChangesModal
        open={unsavedDialogue}
        handleCancel={handleUnsaveCancel}
        handleDiscard={handleUnsaveDiscard}
      />
    </>
  );
};
OptOutModal.propTypes = {
  getList: PropTypes.func,
  data: PropTypes.object,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  isLi: PropTypes.bool,
  userDetails: PropTypes.object,
};

export default OptOutModal;
